<template>
  <div class="chatbot-container">
    <!-- Floating Chat Button -->
    <v-btn
      v-if="!isOpen"
      fab
      class="chat-toggle-btn"
      elevation="4"
      @click="openChat"
    >
      <v-icon>mdi-chat</v-icon>
    </v-btn>

    <!-- Chat Window -->
    <v-card v-if="isOpen" class="chat-window" elevation="8">
      <!-- Chat Header -->
      <v-card-title class="chat-header">
        <v-icon left color="white">mdi-robot</v-icon>
        <span class="white--text">Inventory Assistant</span>

        <v-spacer></v-spacer>

        <!-- Close assistant without clearing messages -->
        <v-btn icon small color="white" @click="closeChat">
          <v-icon>mdi-minus</v-icon>
        </v-btn>

        <!-- Close assistant and clear messages -->
        <v-btn icon small color="white" @click="closeAndClearChat">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <!-- Chat Messages -->
      <div ref="messagesContainer" class="chat-messages">
        <div
          v-for="(message, index) in messages"
          :key="`${message.role}-${index}`"
          class="message"
          :class="message.role"
        >
          <div class="message-content">
            <v-icon
              v-if="message.role === 'assistant'"
              small
              class="message-icon"
              color="primary"
            >
              mdi-robot
            </v-icon>

            <div v-if="message.role === 'user'" class="message-text">
              {{ message.content }}
            </div>

            <div v-else class="message-text assistant-message">
              <vue-markdown
                class="assistant-markdown"
                :source="formatAssistantMessage(message.content)"
                :html="false"
                :xhtml-out="false"
              />
            </div>

            <v-icon
              v-if="message.role === 'user'"
              small
              class="message-icon"
              color="grey"
            >
              mdi-account
            </v-icon>
          </div>
        </div>

        <!-- Typing Indicator -->
        <div v-if="isTyping" class="message assistant">
          <div class="message-content">
            <v-icon small class="message-icon" color="primary">
              mdi-robot
            </v-icon>

            <div class="typing-indicator">
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        </div>
      </div>

      <!-- Chat Input -->
      <div class="chat-input">
        <v-text-field
          v-model="newMessage"
          placeholder="Type your message..."
          outlined
          dense
          hide-details
          :disabled="isTyping"
          append-icon="mdi-send"
          @keydown.enter.prevent="sendMessage"
          @click:append="sendMessage"
        ></v-text-field>
      </div>
    </v-card>
  </div>
</template>

<script>
import api from '@api/api'
import VueMarkdown from 'vue-markdown'

const WELCOME_MESSAGE = {
  role: 'assistant',
  content:
    "Hi! I'm your smart inventory assistant 🤖 I can help you with:\n\n" +
    '• Search for items by name, SKU, or tags\n' +
    '• Check stock levels and low inventory alerts\n' +
    '• Get inventory statistics and reports\n' +
    '• Browse and search categories\n' +
    '• Answer inventory management questions\n\n' +
    'What would you like to know about your inventory?'
}

const DEFAULT_ERROR_MESSAGE =
  "Sorry, I'm having trouble connecting right now. Please try again later."

const RESPONSE_FORMAT_INSTRUCTIONS = `Format the final user-facing answer for a compact chat UI.
- Use Markdown for structure.
- Start with the direct answer or key result.
- Use short sections only when useful, such as "Summary", "Details", "Items", or "Next steps".
- Prefer concise bullets or a small table for lists of inventory data.
- Keep paragraphs to one or two short sentences.
- Do not add blank lines between a heading and the content below it.
- Do not add blank lines between bullet points or numbered list items.
- Use at most one blank line between major sections.
- Do not show internal IDs, raw tool JSON, or implementation details.
- If the answer is simple, keep it to one short paragraph or two to four bullets.
- Continue using the required tool-call JSON format internally when a tool is needed, but make the final answer clean and readable.
- Do not add unnecessary blank lines.`

const DEFAULT_USER = {
  username: '',
  email: '',
  given_name: '',
  family_name: ''
}

export default {
  name: 'ChatbotAssistant',

  components: {
    VueMarkdown
  },

  data() {
    return {
      isOpen: false,
      isTyping: false,
      newMessage: '',
      debugMode: false,
      userEnsured: false,
      userCheckInProgress: false,
      handleAuthStateChange: null,
      currentUser: { ...DEFAULT_USER },
      messages: this.getInitialMessages()
    }
  },

  created() {
    this.setupAuth0Listener()
    this.updateUserFromAuth0()
    this.ensureLocalUserRecord()
    this.waitForAuth0Ready()
  },

  beforeDestroy() {
    this.removeAuth0Listener()
  },

  methods: {
    getInitialMessages() {
      return [{ ...WELCOME_MESSAGE }]
    },

    debugLog(message, data = null) {
      if (!this.debugMode) {
        return
      }

      console.log(`🤖 [ChatBot Debug] ${message}`, data || '')
    },

    openChat() {
      this.isOpen = true
      this.scrollToBottom()
    },

    closeChat() {
      this.isOpen = false
      this.newMessage = ''
    },

    closeAndClearChat() {
      this.isOpen = false
      this.newMessage = ''
      this.isTyping = false
      this.messages = this.getInitialMessages()
    },

    setupAuth0Listener() {
      if (!this.$auth0 || typeof this.$auth0.$on !== 'function') {
        return
      }

      this.handleAuthStateChange = () => {
        this.debugLog('Auth0 state changed')
        this.updateUserFromAuth0()
        this.ensureLocalUserRecord()
      }

      this.$auth0.$on('authStateChanged', this.handleAuthStateChange)
    },

    removeAuth0Listener() {
      if (
        this.$auth0 &&
        typeof this.$auth0.$off === 'function' &&
        this.handleAuthStateChange
      ) {
        this.$auth0.$off('authStateChanged', this.handleAuthStateChange)
      }
    },

    waitForAuth0Ready() {
      if (!this.$auth0?.isLoading) {
        return
      }

      const checkAuth0Ready = () => {
        if (!this.$auth0?.isLoading) {
          this.updateUserFromAuth0()
          this.ensureLocalUserRecord()
          return
        }

        window.setTimeout(checkAuth0Ready, 100)
      }

      checkAuth0Ready()
    },

    getAuth0Claim(claimName) {
      return this.$auth0?.user?.[claimName]
    },

    getOrganizationIdFromAuth0() {
      const orgClaim = this.getAuth0Claim(
        'https://inventory.yourcompany.com/org_id'
      )

      if (Array.isArray(orgClaim)) {
        return orgClaim[0] || null
      }

      return orgClaim || null
    },

    getRolesFromAuth0() {
      const rolesClaim = this.getAuth0Claim(
        'https://inventory.yourcompany.com/roles'
      )

      if (Array.isArray(rolesClaim)) {
        return rolesClaim
      }

      if (rolesClaim) {
        return [rolesClaim]
      }

      return []
    },

    async ensureLocalUserRecord() {
      if (this.userEnsured || this.userCheckInProgress) {
        return
      }

      if (!this.$auth0 || this.$auth0.isLoading || !this.$auth0.user) {
        return
      }

      const orgId = this.getOrganizationIdFromAuth0()
      const username = this.currentUser.username

      if (!orgId) {
        console.warn('No organization id claim found for user.')
        return
      }

      if (!username) {
        console.warn('No username found for Auth0 user.')
        return
      }

      this.userCheckInProgress = true

      try {
        await api.get(
          `/users/organization/${orgId}/username/${encodeURIComponent(
            username
          )}`
        )

        this.userEnsured = true
      } catch (error) {
        if (error?.response?.status !== 404) {
          console.error('Failed checking user existence:', {
            message: error.message,
            status: error.response?.status,
            response: error.response?.data
          })
          return
        }

        await this.createLocalUserRecord(orgId)
      } finally {
        this.userCheckInProgress = false
      }
    },

    async createLocalUserRecord(orgId) {
      const auth0User = this.$auth0?.user

      if (!auth0User) {
        return
      }

      const createPayload = {
        email: auth0User.email || '',
        username: this.currentUser.username,
        roles: this.getRolesFromAuth0(),
        organization_id: orgId
      }

      try {
        await api.post('/users/', createPayload)
        this.userEnsured = true
      } catch (error) {
        console.error('Failed to auto-create local user:', {
          message: error.message,
          status: error.response?.status,
          response: error.response?.data
        })
      }
    },

    async sendMessage() {
      const userMessage = this.newMessage.trim()

      if (!userMessage || this.isTyping) {
        return
      }

      this.newMessage = ''

      this.messages.push({
        role: 'user',
        content: userMessage
      })

      this.scrollToBottom()
      this.isTyping = true

      try {
        const payload = {
          message: this.buildStructuredPrompt(userMessage),
          conversation_history: this.buildConversationHistory(),
          user: this.currentUser
        }

        const response = await api.post('/chat', payload)
        const assistantResponse = response?.data?.response

        this.messages.push({
          role: 'assistant',
          content:
            assistantResponse ||
            'No response received from the assistant. Please try again.'
        })

        if (response?.data?.tools_used?.length) {
          this.debugLog('Tools used:', response.data.tools_used)
        }
      } catch (error) {
        console.error('Chat request failed:', {
          message: error.message,
          status: error.response?.status,
          response: error.response?.data
        })

        this.messages.push({
          role: 'assistant',
          content: DEFAULT_ERROR_MESSAGE
        })
      } finally {
        this.isTyping = false
        this.scrollToBottom()
      }
    },

    buildConversationHistory() {
      return this.messages
        .slice(1, -1)
        .filter((message) => message.role && message.content)
        .map((message) => ({
          role: message.role,
          content: message.content
        }))
    },

    buildStructuredPrompt(userMessage) {
      return `${userMessage}\n\n${RESPONSE_FORMAT_INSTRUCTIONS}`
    },

    formatAssistantMessage(content) {
      return String(content || '')
        .replace(/\r\n/g, '\n')
        .replace(/\n{3,}/g, '\n')
        .replace(/\n\n(?=(?:[-*+]|\d+\.)\s)/g, '\n')
        .replace(/(:)\n\n(?=\S)/g, '$1\n')
        .trim()
    },

    updateUserFromAuth0() {
      if (this.$auth0?.isAuthenticated && this.$auth0?.user) {
        const user = this.$auth0.user

        this.currentUser = {
          username: user.name || user.nickname || user.email || 'Unknown User',
          email: user.email || '',
          given_name: user.given_name || '',
          family_name: user.family_name || ''
        }

        return
      }

      this.currentUser = { ...DEFAULT_USER }
      this.userEnsured = false
    },

    scrollToBottom() {
      this.$nextTick(() => {
        const container = this.$refs.messagesContainer

        if (container) {
          container.scrollTop = container.scrollHeight
        }
      })
    }
  }
}
</script>

<style scoped>
.chatbot-container {
  position: fixed;
  right: 20px;
  bottom: 20px;
  z-index: 1000;
}

.chat-toggle-btn {
  width: 56px !important;
  height: 56px !important;
  background-color: #111 !important;
  color: #fff !important;
}

.chat-toggle-btn:hover {
  background-color: #000 !important;
}

.chat-toggle-btn {
  color: #fff !important;
}

.chat-window {
  width: 350px;
  height: 500px;
  display: flex;
  flex-direction: column;
  border-radius: 12px !important;
  overflow: hidden;
}

.chat-header {
  min-height: auto !important;
  padding: 12px 16px !important;
  color: #fff;
  background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%);
}

.chat-messages {
  flex: 1;
  max-height: 380px;
  padding: 16px;
  overflow-y: auto;
  background: #f5f5f5;
  transition: all 0.3s ease;
}

.message {
  margin-bottom: 12px;
}

.message-content {
  display: flex;
  gap: 8px;
  align-items: flex-end;
}

.message.user .message-content {
  flex-direction: row-reverse;
  justify-content: flex-end;
}

.message-text {
  max-width: 70%;
  padding: 8px 12px;
  word-wrap: break-word;
  white-space: pre-line;
  background: #fff;
  border-radius: 18px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

.message.user .message-text {
  color: #fff;
  background: #1976d2;
}

.message.assistant .message-text {
  max-width: 82%;
  line-height: 1.35;
  color: #333;
  background: #fff;
  border-radius: 14px;
}

.assistant-message {
  overflow-x: auto;
}

.assistant-markdown ::v-deep p {
  margin: 0 0 6px;
}

.assistant-markdown ::v-deep p:last-child,
.assistant-markdown ::v-deep ul:last-child,
.assistant-markdown ::v-deep ol:last-child,
.assistant-markdown ::v-deep table:last-child {
  margin-bottom: 0;
}

.assistant-markdown ::v-deep h1,
.assistant-markdown ::v-deep h2,
.assistant-markdown ::v-deep h3,
.assistant-markdown ::v-deep h4 {
  margin: 2px 0 8px;
  font-size: 0.95rem;
  font-weight: 700;
  line-height: 1.25;
}

.assistant-markdown ::v-deep ul,
.assistant-markdown ::v-deep ol {
  padding-left: 18px;
  margin: 4px 0 6px;
}

.assistant-markdown ::v-deep li {
  margin: 2px 0;
}

.assistant-markdown ::v-deep table {
  width: 100%;
  margin: 8px 0 10px;
  font-size: 0.82rem;
  border-collapse: collapse;
}

.assistant-markdown ::v-deep th,
.assistant-markdown ::v-deep td {
  padding: 6px 8px;
  text-align: left;
  vertical-align: top;
  border: 1px solid #e0e0e0;
}

.assistant-markdown ::v-deep th {
  font-weight: 700;
  background: #f6f8fb;
}

.assistant-markdown ::v-deep code {
  padding: 1px 4px;
  font-size: 0.82em;
  background: #f1f3f5;
  border-radius: 4px;
}

.message-icon {
  margin-bottom: 4px;
}

.chat-input {
  padding: 12px 16px;
  margin: 0;
  background: #fff;
  border-top: 1px solid #e0e0e0;
}

.typing-indicator {
  display: flex;
  gap: 4px;
  padding: 8px 12px;
  background: #fff;
  border-radius: 18px;
}

.typing-indicator span {
  width: 6px;
  height: 6px;
  background: #999;
  border-radius: 50%;
  animation: typing 1.4s infinite ease-in-out;
}

.typing-indicator span:nth-child(1) {
  animation-delay: -0.32s;
}

.typing-indicator span:nth-child(2) {
  animation-delay: -0.16s;
}

@keyframes typing {
  0%,
  80%,
  100% {
    opacity: 0.5;
    transform: scale(0.8);
  }

  40% {
    opacity: 1;
    transform: scale(1);
  }
}

.chat-messages::-webkit-scrollbar {
  width: 4px;
}

.chat-messages::-webkit-scrollbar-track {
  background: transparent;
}

.chat-messages::-webkit-scrollbar-thumb {
  background: #ccc;
  border-radius: 2px;
}

.chat-messages::-webkit-scrollbar-thumb:hover {
  background: #aaa;
}
</style>
